/**
 * @zendir/ui - Progress Component (Pure React)
 *
 * Progress bar following Astro UX Design System patterns.
 *
 * Features:
 * - Theme-integrated via useTheme()
 * - Determinate and indeterminate modes
 * - Optional label and value display
 * - Status-based coloring
 * - Smooth animations
 * - Accessible (progress role with aria attributes)
 */

import React, { memo } from "react";
import { useTheme } from "../theme";
import { classNames, type StatusLevel, formatPercentage } from "../utils";

/** Astro UX status shape per official spec: filled circle (normal), ring (standby), small circle (off), square (caution), diamond (serious), triangle (critical) */
function ProgressStatusShape({
  status,
  color,
  size = 10,
}: {
  status: StatusLevel;
  color: string;
  size?: number;
}): React.ReactElement {
  const glow = `${color}50`;

  switch (status) {
    case "caution":
      return (
        <svg
          viewBox="0 0 12 12"
          width={size}
          height={size}
          style={{ filter: `drop-shadow(0 0 3px ${glow})` }}
        >
          <rect x="1" y="1" width="10" height="10" fill={color} />
        </svg>
      );
    case "serious":
      return (
        <svg
          viewBox="0 0 12 12"
          width={size}
          height={size}
          style={{ filter: `drop-shadow(0 0 3px ${glow})` }}
        >
          <polygon points="6,1 11,6 6,11 1,6" fill={color} />
        </svg>
      );
    case "critical":
      return (
        <svg
          viewBox="0 0 12 12"
          width={size}
          height={size}
          style={{ filter: `drop-shadow(0 0 3px ${glow})` }}
        >
          <polygon points="6,11 1,2 11,2" fill={color} />
        </svg>
      );
    case "standby":
      return (
        <svg
          viewBox="0 0 12 12"
          width={size}
          height={size}
          style={{ filter: `drop-shadow(0 0 3px ${glow})` }}
        >
          <circle
            cx="6"
            cy="6"
            r="3.5"
            fill="none"
            stroke={color}
            strokeWidth="2"
          />
        </svg>
      );
    case "off":
      return (
        <svg
          viewBox="0 0 12 12"
          width={size}
          height={size}
          style={{ filter: `drop-shadow(0 0 3px ${glow})` }}
        >
          <circle cx="6" cy="6" r="3" fill={color} />
        </svg>
      );
    case "normal":
    default:
      return (
        <svg
          viewBox="0 0 12 12"
          width={size}
          height={size}
          style={{ filter: `drop-shadow(0 0 3px ${glow})` }}
        >
          <circle cx="6" cy="6" r="5" fill={color} />
        </svg>
      );
  }
}

export interface ProgressProps {
  /** Current value (0-max) */
  value?: number;
  /** Maximum value (default: 100) */
  max?: number;
  /** Hide the percentage label */
  hideLabel?: boolean;
  /** Show value as label instead of percentage */
  showValue?: boolean;
  /** Status-based coloring (overrides default) */
  status?: StatusLevel;
  /** Indeterminate mode (animated, no value) */
  indeterminate?: boolean;
  /** Height of the progress bar */
  height?: number;
  /** Custom label */
  label?: string;
  /** Show Astro UX status shape icon beneath the bar at the fill position (default: true) */
  showStatusIcon?: boolean;
  /** Custom className */
  className?: string;
}

/**
 * Progress - Pure React progress bar component
 *
 * Displays progress with visual bar and optional label.
 *
 * @example
 * ```tsx
 * <Progress value={85} />
 * <Progress value={42} max={50} hideLabel />
 * <Progress indeterminate label="Loading..." />
 * <Progress value={25} status="caution" />
 * ```
 */
export const Progress = memo(function Progress({
  value,
  max = 100,
  hideLabel = false,
  showValue = false,
  status,
  indeterminate = false,
  height = 8,
  label,
  showStatusIcon = true,
  className = "",
}: ProgressProps): React.ReactElement {
  const { tokens } = useTheme();
  const percentage =
    value !== undefined ? Math.min(100, Math.max(0, (value / max) * 100)) : 0;

  // Auto-determine status based on value if not provided
  const effectiveStatus: StatusLevel =
    status ??
    (percentage >= 75
      ? "normal"
      : percentage >= 50
        ? "standby"
        : percentage >= 25
          ? "caution"
          : percentage > 0
            ? "serious"
            : "off");

  // Get color from theme
  const color = tokens.colors.status[effectiveStatus];

  const displayLabel =
    label ??
    (showValue && value !== undefined
      ? `${value}/${max}`
      : formatPercentage(percentage, false, 0));

  const iconSize = Math.max(10, height + 2);

  return (
    <div
      className={classNames("zendir-progress", className)}
      role="progressbar"
      aria-valuenow={indeterminate ? undefined : value}
      aria-valuemin={0}
      aria-valuemax={max}
      aria-label={label ?? `Progress: ${percentage.toFixed(0)}%`}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: tokens.spacing.xs,
        width: "100%",
      }}
    >
      {/* Label row */}
      {!hideLabel && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            fontSize: tokens.typography.fontSize.sm,
            fontWeight: tokens.typography.fontWeight.medium,
            color: tokens.colors.text.secondary,
          }}
        >
          {label && (
            <span style={{ color: tokens.colors.text.secondary }}>{label}</span>
          )}
          <span
            style={{
              marginLeft: "auto",
              fontVariantNumeric: "tabular-nums",
              color: tokens.colors.text.primary,
            }}
          >
            {!indeterminate && displayLabel}
          </span>
        </div>
      )}

      {/* Progress track (relative wrapper for icon positioning) */}
      <div style={{ position: "relative", width: "100%" }}>
        <div
          style={{
            position: "relative",
            width: "100%",
            height,
            backgroundColor: tokens.colors.border.muted,
            borderRadius: height / 2,
            overflow: "hidden",
          }}
        >
          {/* Progress fill */}
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              height: "100%",
              width: indeterminate ? "30%" : `${percentage}%`,
              backgroundColor: color,
              borderRadius: height / 2,
              transition: indeterminate ? "none" : "width 300ms ease-out",
              animation: indeterminate
                ? "progressIndeterminate 1.5s ease-in-out infinite"
                : "none",
              boxShadow: `0 0 ${height}px ${color}40`,
            }}
          />
        </div>

        {/* Status shape icon positioned beneath the fill edge */}
        {showStatusIcon && !indeterminate && (
          <div
            style={{
              position: "absolute",
              top: height + 4,
              left: `${percentage}%`,
              transform: "translateX(-50%)",
              lineHeight: 0,
              transition: "left 300ms ease-out",
            }}
            aria-hidden="true"
          >
            <ProgressStatusShape
              status={effectiveStatus}
              color={color}
              size={iconSize}
            />
          </div>
        )}
      </div>

      {/* Reserve space for the icon so it doesn't overlap content below */}
      {showStatusIcon && !indeterminate && (
        <div style={{ height: iconSize + 2 }} />
      )}

      {/* Keyframe animation for indeterminate */}
      <style>{`
        @keyframes progressIndeterminate {
          0% { left: -30%; }
          100% { left: 100%; }
        }
      `}</style>
    </div>
  );
});
